import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { formatPrice, type ConfiguratorOption } from "../../features/vault/configurator";
import { SlotBar, type SlotBarChoice } from "./SlotBar";
import { choicesOf } from "./swatch";

/**
 * What a SHOPPER sees: the glass bar along the bottom of the shared link —
 * one pill per part (plus "Model" when the piece comes as several models),
 * the active pill's swatches above it, and the piece's name and running
 * price on top.
 *
 * One component for both places a shopper's view is drawn — the shared link
 * (`app/ConfiguratorView.tsx`) and the studio's Preview as customer
 * (`ui/editor/configurator/CustomerPreview.tsx`) — so the preview is the link,
 * pixel for pixel, not a lookalike. Presentational: the host decides what a
 * pick does to the model.
 */

/** A part a shopper can change, with the choice it is showing. */
export interface CustomerBarGroup {
  id: string;
  label: string;
  options: ConfiguratorOption[];
  activeIndex: number;
}

/** A whole-model choice (another piece). */
export interface CustomerBarVariant {
  id: string;
  label: string;
  thumbUrl?: string;
}

/** The bar's pill for the whole-model choice, when the def offers variants. */
const MODEL_SLOT = "__model";

export function CustomerBar({
  name,
  groups,
  variants = [],
  activeVariant = 0,
  swapping = false,
  basePrice,
  currency = "USD",
  disabled = false,
  onPick,
  onVariant,
}: {
  name?: string;
  groups: CustomerBarGroup[];
  variants?: CustomerBarVariant[];
  activeVariant?: number;
  /** A model swap is in flight. */
  swapping?: boolean;
  /** Absent: no price is shown. */
  basePrice?: number;
  currency?: string;
  disabled?: boolean;
  onPick: (groupId: string, index: number) => void;
  onVariant?: (index: number) => void;
}) {
  /** The pill whose swatches are showing. */
  const [activeSlot, setActiveSlot] = useState<string | null>(null);
  const reduce = useReducedMotion();

  const showPrice = basePrice != null;
  const price = (basePrice ?? 0) + groups.reduce((s, g) => s + (g.options[g.activeIndex]?.priceDelta ?? 0), 0);

  // A pill per configurable part (+ "Model" when the piece comes as several
  // models), and the active pill's swatches above it.
  const slots = [
    ...(variants.length > 1 ? [{ key: MODEL_SLOT, label: "Model" }] : []),
    ...groups.map((g) => ({ key: g.id, label: g.label })),
  ];
  // Opens on the first PART (that is what most shoppers came to change); the
  // model pill stays one tap away.
  const shownSlot = slots.find((s) => s.key === activeSlot)?.key ?? groups[0]?.id ?? slots[0]?.key ?? null;
  const shownGroup = groups.find((g) => g.id === shownSlot);
  const choices: SlotBarChoice[] =
    shownSlot === MODEL_SLOT
      ? variants.map((v, i) => ({
          key: v.id,
          name: v.label,
          fill: v.thumbUrl ? { backgroundImage: `url(${JSON.stringify(v.thumbUrl)})` } : { backgroundColor: "#E8E1D2" },
          active: activeVariant === i,
          busy: swapping && activeVariant === i,
        }))
      : choicesOf(shownGroup?.options ?? [], shownGroup?.activeIndex ?? -1);
  const pick = (key: string) => {
    if (shownSlot === MODEL_SLOT) {
      const vi = variants.findIndex((v) => v.id === key);
      if (vi >= 0) onVariant?.(vi);
      return;
    }
    const idx = shownGroup?.options.findIndex((o) => o.id === key) ?? -1;
    if (shownGroup && idx >= 0) onPick(shownGroup.id, idx);
  };

  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="pointer-events-none absolute inset-x-0 bottom-0 z-30 flex justify-center p-3 sm:p-5"
      data-customer-bar
    >
      <SlotBar
        tone="glass"
        ariaLabel="Choose your materials"
        slots={slots}
        activeSlot={shownSlot}
        onSlot={setActiveSlot}
        choices={choices}
        onChoice={pick}
        disabled={disabled || (shownSlot === MODEL_SLOT && swapping)}
        header={
          showPrice || name ? (
            <div className="flex min-w-[220px] items-baseline justify-between gap-4 px-1">
              <span className="truncate text-[14px] font-semibold text-ink-900">{name}</span>
              {showPrice && (
                <AnimatePresence mode="popLayout">
                  <motion.span
                    key={price}
                    initial={reduce ? false : { opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={reduce ? undefined : { opacity: 0, y: 6 }}
                    transition={{ duration: 0.2 }}
                    className="shrink-0 text-[19px] font-semibold tracking-tight text-ink-900"
                  >
                    {formatPrice(price, currency)}
                  </motion.span>
                </AnimatePresence>
              )}
            </div>
          ) : undefined
        }
      />
    </motion.div>
  );
}
